import { useEffect, useRef, type JSX } from 'react'
import { Bot, Check, Clock, Headset, User, X } from 'lucide-react'
import { useNavigate } from 'react-router'
import { Dialog } from 'primereact/dialog'
import { useFocusTrap } from '../../hooks/useFocusTrap.js'
import { useChatOfferStore } from '../../store/chatOfferStore.js'
import { useToast } from '../system/ToastProvider.js'
import { playNotificationChime } from '../../utils/notificationSound.js'
import { showBrowserNotification } from '../../utils/browserNotifications.js'
import { api } from '../../api/client.js'
import { timestampMs } from '../../utils/timezone.js'

// If nothing arrives within this bound after Accept, don't hang indefinitely
// on a lost `chat:offer:accepted` event — clear the popup and let the agent
// find the conversation in their chat list (per the plan's resolved design).
// 20s (not the originally-planned 10s): the accept API call only inserts an
// outbox row — it can take up to ~5s for apps/worker's outbox-processor
// cron to drain it, plus normal processing time, before
// `widget.escalation.assigned` actually gets relayed back as
// `chat:offer:accepted`. 10s left too little margin and could show a false
// "Connection timed out" toast while a real accept was still succeeding in
// the background.
const CONNECTING_TIMEOUT_MS = 20_000

// Below this many seconds remaining, the countdown text turns red instead
// of amber — a last-chance visual cue before the offer expires server-side.
const URGENT_THRESHOLD_SECONDS = 5

function secondsUntil(isoExpiresAt: string): number {
  const ms = timestampMs(isoExpiresAt) - Date.now()
  return Math.max(0, Math.ceil(ms / 1000))
}

/**
 * Global, app-wide popup shown to an agent when the chat-escalation offer
 * state machine (apps/worker's BotOrchestrator) offers them a conversation.
 * Mirrors SoftphoneOverlay's structure (PrimeReact Dialog, focus trap,
 * ESC-to-close = Decline). Two phases:
 *  - 'offered': shows the bot transcript + Accept/Decline.
 *  - 'connecting': entered the instant Accept is clicked; shows a spinner
 *    until `chat:offer:accepted` (relayed via the existing
 *    widget.escalation.assigned event) supplies the conversationId, at which
 *    point this component navigates into the conversation and clears the
 *    store.
 *
 * On every offer arrival, plays a chime + fires a desktop Notification (if
 * permission was previously granted — requested elsewhere in the app, see
 * apps/web/src/utils/browserNotifications.ts) so an agent who isn't looking
 * at this tab still notices. Every failure path (accept/decline API error,
 * connecting-phase timeout) surfaces a toast instead of silently closing.
 */
export default function ChatOfferPopup(): JSX.Element | null {
  const navigate = useNavigate()
  const toast = useToast()
  const pendingOffer = useChatOfferStore((s) => s.pendingOffer)
  const isReassign = pendingOffer?.reassign === true
  const isConversation = typeof pendingOffer?.conversationId === 'string'
  const phase = useChatOfferStore((s) => s.phase)
  const acceptedConversationId = useChatOfferStore((s) => s.acceptedConversationId)
  const secondsRemaining = useChatOfferStore((s) => s.secondsRemaining)

  const visible = pendingOffer !== null
  const trapRef = useFocusTrap<HTMLDivElement>(visible)

  // Alert (chime + desktop notification) exactly once per distinct offer —
  // keyed on offerId so re-renders of the SAME offer never re-alert.
  const alertedOfferIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!pendingOffer) {
      alertedOfferIdRef.current = null
      return
    }
    if (alertedOfferIdRef.current === pendingOffer.offerId) return
    alertedOfferIdRef.current = pendingOffer.offerId
    playNotificationChime()
    showBrowserNotification(
      isReassign ? 'Reassigned live chat' : 'New chat request',
      isReassign
        ? 'A live chat needs a new agent because the previous one went offline.'
        : 'A visitor is waiting to talk to an agent.',
    )
  }, [pendingOffer])

  // Live countdown to pendingOffer.expiresAt, ticked every second while in
  // the 'offered' phase (no countdown needed once accepted/connecting).
  useEffect(() => {
    if (!pendingOffer || phase !== 'offered') return
    const tick = () => useChatOfferStore.getState().setSecondsRemaining(secondsUntil(pendingOffer.expiresAt))
    tick()
    const intervalId = setInterval(tick, 1000)
    return () => clearInterval(intervalId)
  }, [pendingOffer, phase])

  // Navigate once the accepted conversationId arrives while connecting.
  useEffect(() => {
    if (phase !== 'connecting' || !acceptedConversationId) return
    const channelChat = useChatOfferStore.getState().pendingOffer?.channelChat
    if (channelChat) {
      navigate(`/agent/chats/${channelChat.channel}/${acceptedConversationId}`)
    } else if (isConversation) {
      navigate(`/agent/conversation/view/${acceptedConversationId}`)
    } else {
      navigate(`/agent/chat?conversationId=${acceptedConversationId}&channel=chat`)
    }
    useChatOfferStore.getState().clearOffer()
  }, [phase, acceptedConversationId, navigate, isConversation])

  // Fallback: don't hang indefinitely on a lost chat:offer:accepted event.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (phase !== 'connecting') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return
    }
    timeoutRef.current = setTimeout(() => {
      // Only warn if we're still waiting — if acceptedConversationId showed
      // up in the same tick that cleared this timer, the effect above's
      // cleanup already ran and this callback is never invoked at all.
      toast.warning('Connection timed out', 'Check your chat — this conversation may already be waiting for you.')
      useChatOfferStore.getState().clearOffer()
    }, CONNECTING_TIMEOUT_MS)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [phase, toast])

  function handleDecline(): void {
    const offer = useChatOfferStore.getState().pendingOffer
    useChatOfferStore.getState().clearOffer()
    if (offer) {
      void api
        .post(`/agent/chat-offers/${offer.offerId}/respond`, { response: 'declined' })
        .catch(() => {
          toast.error('Failed to decline chat request', 'The offer will still expire on its own shortly.')
        })
    }
  }

  function handleAccept(): void {
    const offer = useChatOfferStore.getState().pendingOffer
    if (!offer) return
    useChatOfferStore.getState().setConnecting()
    void api
      .post(`/agent/chat-offers/${offer.offerId}/respond`, { response: 'accepted' })
      .then(() => {
        const targetId =
          offer.channelChat?.chatId ||
          offer.conversationId ||
          offer.conversationAttemptId
        if (targetId) {
          useChatOfferStore.getState().setAccepted(targetId)
        }
      })
      .catch(() => {
        toast.error('Failed to accept chat request', 'Please try again.')
        useChatOfferStore.getState().clearOffer()
      })
  }

  if (!pendingOffer) return null

  const countdownIsUrgent = secondsRemaining !== null && secondsRemaining <= URGENT_THRESHOLD_SECONDS

  return (
    <Dialog
      visible={visible}
      onHide={handleDecline}
      header={isReassign ? 'Reassigned live chat' : 'New chat request'}
      modal
      style={{ width: '26rem' }}
    >
      <div ref={trapRef} data-testid="chat-offer-focus-trap" className="flex flex-col gap-5">
        {phase === 'connecting' ? (
          <div className="flex flex-col items-center gap-4 py-10">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
            <p className="text-sm font-medium text-slate-700">Connecting you to the visitor…</p>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isReassign ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                <Headset size={20} aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{isReassign ? 'Take over live chat' : 'Incoming chat request'}</p>
                <p className="text-xs text-slate-500">
                  {isReassign
                    ? 'Previous agent went offline — this chat needs a new agent.'
                    : 'A visitor is waiting for assistance.'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
              {pendingOffer.visitorName || pendingOffer.visitorEmail ? (
                <div className="min-w-0">
                  {pendingOffer.visitorName && (
                    <p className="truncate text-sm font-semibold text-slate-900">{pendingOffer.visitorName}</p>
                  )}
                  {pendingOffer.visitorEmail && (
                    <p className="truncate text-xs text-slate-500">{pendingOffer.visitorEmail}</p>
                  )}
                </div>
              ) : (
                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">
                  Guest
                </span>
              )}
            </div>

            {secondsRemaining !== null && (
              <div className="flex items-center justify-end gap-1.5">
                <Clock size={14} aria-hidden="true" className={countdownIsUrgent ? 'text-red-600' : 'text-amber-600'} />
                <span
                  className={`text-xs font-semibold ${countdownIsUrgent ? 'text-red-600' : 'text-amber-600'}`}
                  aria-live="polite"
                >
                  Expires in {secondsRemaining}s
                </span>
              </div>
            )}

            <div className="max-h-56 space-y-3 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3">
              {pendingOffer.transcript.map((line, idx) => (
                <div
                  key={idx}
                  className={`flex gap-2 ${line.from === 'visitor' ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      line.from === 'visitor' ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {line.from === 'visitor' ? <User size={14} aria-hidden="true" /> : <Bot size={14} aria-hidden="true" />}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                      line.from === 'visitor'
                        ? 'rounded-tl-none bg-blue-600 text-white'
                        : 'rounded-tr-none bg-white text-slate-700 shadow-sm'
                    }`}
                  >
                    {line.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleDecline}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
              >
                <X size={16} aria-hidden="true" />
                Decline
              </button>
              <button
                type="button"
                onClick={handleAccept}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Check size={16} aria-hidden="true" />
                Accept
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}
